/**
 * Página de callback OAuth
 * Recibe el código de autorización y lo envía a la ventana padre
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const OAuthCallback: React.FC = () => {
  const { platform } = useParams<{ platform: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Procesando autorización...');

  useEffect(() => {
    const handleCallback = () => {
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      const shop = searchParams.get('shop'); // Para Shopify
      const state = searchParams.get('state'); // Para verificar CSRF

      if (error) {
        setStatus('error');
        setMessage(errorDescription || 'Error de autorización');
        
        // Notificar a la ventana padre del error
        if (window.opener) {
          window.opener.postMessage(
            {
              type: `oauth_callback_${platform}`,
              error: true,
              errorMessage: errorDescription || 'Error de autorización',
            },
            window.location.origin
          );
        }

        // Cerrar ventana después de 3 segundos
        setTimeout(() => {
          window.close();
        }, 3000);
        return;
      }

      if (!code) {
        setStatus('error');
        setMessage('No se recibió el código de autorización');

        setTimeout(() => {
          window.close();
        }, 3000);
        return;
      }

      // Enviar código a la ventana padre
      setStatus('success');
      setMessage('Autorización exitosa. Cerrando ventana...');

      if (window.opener) {
        window.opener.postMessage(
          {
            type: `oauth_callback_${platform}`,
            code,
            shop, // Incluir para Shopify
            state, // Incluir para validación CSRF
            error: false,
          },
          window.location.origin
        );

        // Cerrar ventana después de 1 segundo
        setTimeout(() => {
          window.close();
        }, 1000);
      } else {
        // Si no hay ventana padre, redirigir a la app
        setMessage('Autorización exitosa. Redirigiendo...');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    };

    handleCallback();
  }, [platform, searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="w-16 h-16 text-primary-600 dark:text-primary-400 mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Procesando...
            </h2>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-600 dark:text-green-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              ¡Autorización Exitosa!
            </h2>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-600 dark:text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Error de Autorización
            </h2>
          </>
        )}

        <p className="text-gray-600 dark:text-gray-300 mb-6">
          {message}
        </p>

        <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
          <span>Plataforma:</span>
          <span className="font-semibold capitalize">{platform}</span>
        </div>
      </div>
    </div>
  );
};

export default OAuthCallback;

